import { type ChangeEventHandler, useState } from 'react'

import type { SelectOptionsType } from '@/components/ui/form/FormSelect'
import { Skeleton } from '@/components/ui/skeleton'
import { classNames } from '@/utils/helpers'

import SearchIcon from '../../icons/search.svg'
import SmallCheckIcon from '../../icons/small-check.svg'

interface Props {
  options: SelectOptionsType
  value: string
  onChange: (value: string) => void
  label: string
  disabled?: boolean
  className?: string
  isLoading?: boolean
  skeletonClassName?: string
}

export const StaticSelect = ({
  options,
  value,
  onChange,
  label,
  disabled,
  className,
  isLoading,
  skeletonClassName,
}: Props) => {
  const [search, setSearch] = useState('')

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  )

  const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearch(event.target.value)
  }

  return (
    <div className={className}>
      <label className="text-title mb-[6px] block text-sm font-medium">
        {label}
      </label>
      {isLoading ? (
        <Skeleton className={skeletonClassName} />
      ) : (
        <div className="wide-scrollbar visible-scrollbar-track border-border-secondary rounded-md border p-2">
          <div className="border-border-secondary mb-[10px] flex items-center gap-2 border-b border-solid px-3 py-2">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search"
              className="flex-1 bg-card text-sm leading-5 text-title placeholder:text-secondary-300 focus:outline-none"
              onChange={handleChangeSearch}
            />
          </div>
          <div className="max-h-[340px] overflow-auto pr-[8px]">
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => {
                  if (option.label !== value) {
                    onChange(option.label)
                  }
                }}
                className={classNames(
                  'flex cursor-pointer items-center justify-between rounded-md px-3 py-[6px] text-sm font-medium leading-[22px] text-title',
                  {
                    'bg-primary-500 text-white': value === option.label,
                    'hover:bg-secondary-50 dark:hover:bg-secondary-600':
                      value !== option.label,
                  },
                )}
              >
                {option.label}
                {value === option.label && (
                  <SmallCheckIcon className="text-white" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
